import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import HiddenT90Report from './HiddenT90Report';
import Acumen_25_Years_LOGO from '../assets/Acumen_25_Years_LOGO.png';

// Logo + cover shape now match VakSurvey.jsx exactly (same SVG panel, same
// asset import — swap the import path above if Transition 90 uses a
// different logo file than VAK in your assets folder).

const T90Report = () => {
  const location = useLocation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [submissionTime, setSubmissionTime] = useState(null);
  const generatingRef = useRef(false);

  // ── Trigger HiddenT90Report PDF generation ─────────────────────────────
  const handleDownloadPDF = () => {
    if (generatingRef.current) return;   // synchronous guard — fires before any re-render
    generatingRef.current = true;
    setIsGenerating(true);
    const normalized = reportData?.data || reportData;
    setPdfData({
      ...normalized,
      submission_time: submissionTime,
    });
  };

  // Stable callback — avoids creating a new function reference on every render,
  // which could otherwise cause HiddenT90Report to re-run its effect.
  const handlePdfComplete = useCallback(() => {
    setPdfData(null);
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  useEffect(() => {
    if (location.state?.scores) {
      const data = location.state.scores;
      setReportData(data);
      const normalized = data?.data || data;
      setSubmissionTime(normalized?.respondent?.submission_time ?? location.state?.submission_time ?? new Date().toISOString());
      setLoading(false);
    } else {
      const fetchFallback = async () => {
        try {
          const email = location.state?.email;
          const phone = location.state?.phone;
          const response = await fetch('/calculate-score/t90', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, phone }),
          });
          if (!response.ok) throw new Error('Could not retrieve report data.');
          const result = await response.json();
          setReportData(result);
          setSubmissionTime(result?.data?.respondent?.submission_time ?? new Date().toISOString());
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchFallback();
    }
  }, [location.state]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'Satoshi, sans-serif' }}>Loading Report...</div>;
  if (error) return <div style={{ padding: '2rem', color: 'red', fontFamily: 'Satoshi, sans-serif' }}>Error: {error}</div>;
  if (!reportData) return <div style={{ padding: '2rem', fontFamily: 'Satoshi, sans-serif' }}>No data found. Please retake the assessment.</div>;

  // Normalize: handles both { data: {...} } (raw API response) and the
  // unwrapped {...} shape (if a caller already stored `result.data`).
  const normalized   = reportData?.data || reportData;
  const respondent    = normalized?.respondent || {};
  const overall        = normalized?.overall || { pct: 0 };
  const mindset          = normalized?.mindset || { pct: 0, dimensions: [] };
  const skill              = normalized?.skill || { pct: 0, dimensions: [] };
  const radar                = normalized?.radar || { mindset: { codes: [], values: [] }, skill: { codes: [], values: [] } };

  const dateStr = submissionTime
    ? new Date(submissionTime).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  // v2 — revised scoring bands: 0–39 / 40–59 / 60–79 / 80–100%
  const bandInfo = (pct) => {
    if (pct >= 80) return { level: "strong", label: "Core Strength" };
    if (pct >= 60) return { level: "estab", label: "Established Strength" };
    if (pct >= 40) return { level: "emerging", label: "Emerging Strength" };
    return { level: "dev", label: "Development Priority" };
  };

  const readinessNarrative = (overallPct, mindsetPct, skillPct) => {
    const gap = skillPct - mindsetPct;
    const overallBand = bandInfo(overallPct).label.toLowerCase();

    if (Math.abs(gap) < 8) {
      return `This is a balanced profile — the belief system and the practical judgement are developing in step with each other, which is the healthiest pattern to see at this stage. Neither score is running ahead of or masking the other, so there's no hidden gap for pressure to expose later. Overall readiness sits at ${overallPct}% (${overallBand}), and coaching time is best spent lifting both layers together on the specific dimensions flagged below, rather than favouring one over the other.`;
    }

    if (gap >= 8) {
      return `This is a solidifying profile, with the practical judgement running ahead of an already-developing belief system — this participant is executing well above what the mindset score alone would predict. That's a strong signal for readiness: the behaviour is consistent, and the belief system underneath it — while not yet at the same level — is what's worth reinforcing next. Overall readiness sits at ${overallPct}% (${overallBand}), and coaching time is better spent closing the specific remaining mindset gaps below than on foundational rebuilding.`;
    }

    return `This is a profile where the belief system is running ahead of the practical judgement — the underlying mindsets are more developed than the situational skill scores currently show. That's a useful distinction: this isn't a belief problem, it's an execution gap, which usually closes faster with structured practice and real-world reps than with more conceptual coaching. Overall readiness sits at ${overallPct}% (${overallBand}), and the fastest path forward is deliberate practice on the specific skill dimensions flagged below.`;
  };

  const chipColor = (level) => {
    switch (level) {
      case 'strong':   return { bg: '#eaf3e6', fg: '#2a5010' };
      case 'estab':    return { bg: '#e3eaf5', fg: '#3a5a8a' };
      case 'emerging': return { bg: '#fdf3e0', fg: '#8a5a00' };
      default:         return { bg: '#fbeaea', fg: '#a81026' };
    }
  };

  // ── Minimal dependency-free radar chart (SVG), same visual role as the
  //    Plotly radar in transition-90-assessment.html ─────────────────────
  const Radar = ({ series, size = 420 }) => {
    const top = series[0] || { codes: [], values: [] };
    const bottom = series[1] || { codes: [], values: [] };
    const M = top.codes.length;
    const S = bottom.codes.length;
    if (!M && !S) return null;
    const cx = size / 2, cy = size / 2;
    const R = size / 2 - 60;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const pointAt = (angleDeg, val) => {
      const r = (Math.max(0, Math.min(100, val)) / 100) * R;
      const a = toRad(angleDeg);
      return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
    };
    const pointAtFrac = (angleDeg, frac) => {
      const r = frac * R;
      const a = toRad(angleDeg);
      return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
    };
    // Single continuous 360° wheel: M1 sits at the top and the M axes run
    // clockwise, then the S axes continue clockwise around the rest of the
    // circle back to just before M1 — matching the reference chart.
    const N = M + S;
    const unit = N ? 360 / N : 0;
    const topAngle = (i) => 90 - i * unit;
    const bottomAngle = (i) => 90 - (M + i) * unit;
    const ringLevels = [20, 40, 60, 80, 100];
    const topPts = top.values.map((v, i) => pointAt(topAngle(i), v));
    const bottomPts = bottom.values.map((v, i) => pointAt(bottomAngle(i), v));

    return (
      <svg width="100%" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Dimension radar chart">
        {ringLevels.map((lvl) => (
          <circle
            key={lvl}
            cx={cx}
            cy={cy}
            r={(lvl / 100) * R}
            fill="none"
            stroke={lvl === 100 ? "#8a94a3" : "#e2e8f0"}
            strokeWidth={lvl === 100 ? "1.3" : "0.9"}
          />
        ))}
        {top.codes.map((code, i) => {
          const a = topAngle(i);
          const [x, y] = pointAtFrac(a, 1.03);
          const [lx, ly] = pointAtFrac(a, 1.14);
          return (
            <g key={`t-${code}`}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke="#c7ccd4" strokeWidth="0.8" />
              <text x={lx} y={ly} fontSize="11" fill="#A31621" textAnchor="middle" dominantBaseline="middle">{code}</text>
            </g>
          );
        })}
        {bottom.codes.map((code, i) => {
          const a = bottomAngle(i);
          const [x, y] = pointAtFrac(a, 1.03);
          const [lx, ly] = pointAtFrac(a, 1.14);
          return (
            <g key={`b-${code}`}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke="#c7ccd4" strokeWidth="0.8" />
              <text x={lx} y={ly} fontSize="11" fill="#F5A623" textAnchor="middle" dominantBaseline="middle">{displayCode(code)}</text>
            </g>
          );
        })}
        {/* Bold radial axis line carrying the 0/20/40/60/80/100 ticks, running up the M1 axis at the top, matching the Plotly reference */}
        <line x1={cx} y1={cy} x2={cx} y2={cy - R} stroke="#4b5563" strokeWidth="0.9" />
        {[0, 20, 40, 60, 80, 100].map((v) => {
          const [x, y] = pointAt(90, v);
          return (
            <text key={v} x={x + 6} y={y} fontSize="10" fill="#8a94a3" textAnchor="start" dominantBaseline="middle">{v}</text>
          );
        })}
        {M > 0 && (
          <polygon points={topPts.map((p) => p.join(',')).join(' ')} fill={top.fill} stroke={top.stroke} strokeWidth="2" />
        )}
        {S > 0 && (
          <polygon points={bottomPts.map((p) => p.join(',')).join(' ')} fill={bottom.fill} stroke={bottom.stroke} strokeWidth="2" />
        )}
        {topPts.map((p, i) => (
          <circle key={`td-${i}`} cx={p[0]} cy={p[1]} r="2.6" fill={top.stroke} />
        ))}
        {bottomPts.map((p, i) => (
          <circle key={`bd-${i}`} cx={p[0]} cy={p[1]} r="2.6" fill={bottom.stroke} />
        ))}
      </svg>
    );
  };

  // Display-only relabeling of Skill IDs. This ONLY changes what is shown on
  // screen — d.code itself (used for keys, scoring, and internal lookups)
  // is never touched.
  const SKILL_ID_DISPLAY_MAP = {
    S5: "S4",
    S6: "S5",
    S7: "S6",
    S9: "S7",
    S10: "S8",
    S11: "S9",
    S12: "S10",
    S13: "S11",
    S14: "S12",
  };
  const displayCode = (code) => {
    if (SKILL_ID_DISPLAY_MAP[code]) return SKILL_ID_DISPLAY_MAP[code];
    return code;
  };

  // Fixed-size SVG donut for the Overall Readiness score.
  const Donut = ({ pct, size = 220 }) => {
    const r = 92;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (Math.max(0, Math.min(100, pct)) / 100) * circumference;
    const cx = size / 2, cy = size / 2;
    return (
      <div className="donut-wrap">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eee" strokeWidth="20" />
          <circle
            cx={cx} cy={cy} r={r} fill="none" stroke="url(#gradReadiness)"
            strokeWidth="20" strokeLinecap="round"
            strokeDasharray={circumference.toFixed(1)}
            strokeDashoffset={offset.toFixed(1)}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
          <defs>
            <linearGradient id="gradReadiness" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A80D2C" />
              <stop offset="100%" stopColor="#D79A1E" />
            </linearGradient>
          </defs>
        </svg>
        <div className="donut-center">
          <div className="pct">{pct}%</div>
          <div className="lbl">Overall Readiness</div>
        </div>
      </div>
    );
  };

  const renderDimensionTable = (dims) => (
    <div className="dim-table">
      {dims.map((d) => {
        const c = chipColor(d.interpretation.level);
        return (
          <div className="dim-row" key={d.code}>
            <div className="dim-name">
              <strong>{d.label}</strong>
              <span>{displayCode(d.code)}</span>
            </div>
            <div className="dim-bar-wrap">
              <div className="dim-bar-track"><div className="dim-bar-fill" style={{ width: `${d.pct}%` }} /></div>
              <span className="dim-pct">{d.pct}%</span>
            </div>
            <div className="dim-chip" style={{ background: c.bg, color: c.fg }}>{d.interpretation.label}</div>
          </div>
        );
      })}
    </div>
  );

  // ── v2 — Mindset ↔ Skill map + Strengths/Priorities synthesis helpers ──
  const MINDSET_SKILL_MAP = [
    { m: "M1",  mLabel: "Letting Go",                              s: ["S1"],        sLabel: "S1 Delegation",                     why: "Letting go is the belief; delegation is the behaviour it enables" },
    { m: "M2",  mLabel: "Handling Insecurities",                   s: ["S2","S14"],  sLabel: "S2 Feedback, S12 Influencing",      why: "Both require staying non-defensive while exposed to judgement" },
    { m: "M3",  mLabel: "Creating Boundaries",                     s: ["S6"],        sLabel: "S5 Setting Priorities",             why: "Boundaries are what make real prioritisation stick" },
    { m: "M4",  mLabel: "Managing Power Awkwardness",               s: ["S3"],        sLabel: "S3 Building Trust",                 why: "Both concern navigating an uneven power dynamic honestly" },
    { m: "M5",  mLabel: "Comfort with Ambiguity",                  s: ["S7"],        sLabel: "S6 Team Planning",                  why: "Planning under incomplete information requires tolerating ambiguity" },
    { m: "M6",  mLabel: "Thinking in Outcomes",                    s: ["S11"],       sLabel: "S9 Monitoring Performance",        why: "Reviewing outcomes requires already thinking in outcome terms" },
    { m: "M7",  mLabel: "Pride in Others Surpassing You",          s: ["S9","S10"],  sLabel: "S7 Spotting Talent, S8 Coaching",  why: "Both require genuinely wanting others to outgrow you" },
    { m: "M8",  mLabel: "Shifting from 'I' to 'We'",                s: ["S12"],       sLabel: "S10 Team Building",                 why: "Team-building is impossible while still thinking individually" },
    { m: "M9",  mLabel: "Trusting Team Over Controlling Output",   s: ["S9","S10"],  sLabel: "S7 Spotting Talent, S8 Coaching",  why: "Trust is the precondition for developing others" },
    { m: "M10", mLabel: "Holding People Accountable",              s: ["S11"],       sLabel: "S9 Monitoring Performance",        why: "Accountability requires the discipline to track and follow up" },
    { m: "M11", mLabel: "Seeing the Org as a System",               s: ["S13","S14"], sLabel: "S11 Managing Stakeholders, S12 Influencing", why: "Systems-thinking is what stakeholder and influence work runs on" },
    { m: "M12", mLabel: "Believing in Self",                       s: ["S5"],        sLabel: "S4 Being Assertive",                why: "Self-belief is the prerequisite for holding a position under pressure" },
  ];

  const joinList = (items) => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
  };

  const mindsetDims = mindset.dimensions || [];
  const skillDims = skill.dimensions || [];

  const coreStrengthLabel = (d) => `${d.label} (${d.pct}%)`;
  const mindsetCoreStrengths = mindsetDims.filter((d) => d.pct >= 80);
  const skillCoreStrengths = skillDims.filter((d) => d.pct >= 80);

  const lowestMindset = mindsetDims.length
    ? mindsetDims.reduce((min, d) => (d.pct < min.pct ? d : min), mindsetDims[0])
    : null;
  const lowestSkillPct = skillDims.length ? Math.min(...skillDims.map((d) => d.pct)) : null;
  const lowestSkills = skillDims.filter((d) => d.pct === lowestSkillPct);

  const linkedRow = lowestMindset ? MINDSET_SKILL_MAP.find((r) => r.m === lowestMindset.code) : null;
  const linkedSkillDims = linkedRow ? skillDims.filter((d) => linkedRow.s.includes(d.code)) : [];
  const isLinkedAlsoLowestSkill =
    linkedSkillDims.length > 0 &&
    lowestSkillPct !== null &&
    linkedSkillDims.every((d) => d.pct === lowestSkillPct);
  // Split linked skill dimensions into weak (<50%, i.e. below the midpoint)
  // and strong (>=50%) groups. A mindset dimension can link to MORE THAN ONE
  // skill dimension (e.g. M2 -> S2, S14), and those can land on opposite
  // sides of the midpoint — so we can't just check "all" or "none" without
  // misdescribing the mixed case.
  const weakLinkedSkillDims = linkedSkillDims.filter((d) => d.pct < 50);
  const strongLinkedSkillDims = linkedSkillDims.filter((d) => d.pct >= 50);
  const linkedBelowMidpoint = linkedSkillDims.length > 0 && strongLinkedSkillDims.length === 0;
  const linkedShowsMatchingGap = weakLinkedSkillDims.length > 0;
  const secondaryPriorities = skillDims.filter(
    (d) => d.pct < 40 && !linkedSkillDims.some((ld) => ld.code === d.code)
  );

  return (
    <>
      <div className="t90-report" id="t90-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
        :root {
          --text-xs: clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem);
          --text-sm: clamp(0.875rem, 0.8rem + 0.35vw, 1rem);
          --text-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
          --text-lg: clamp(1.125rem, 1rem + 0.75vw, 1.4rem);
          --text-xl: clamp(1.45rem, 1.2rem + 1.1vw, 2rem);
          --space-1: 0.25rem; --space-2: 0.5rem; --space-3: 0.75rem; --space-4: 1rem;
          --space-5: 1.25rem; --space-6: 1.5rem; --space-8: 2rem; --space-10: 2.5rem;
          --space-12: 3rem; --space-16: 4rem; --space-20: 5rem;
          --navy: #0f2744; --blue: #c62828; --gold: #d6a23f;
          --ink: #1f2937; --muted: #6b7280;
          --bg: #f4f6f8; --card: #ffffff; --line: #d9e0e7; --soft: #eef2f6;
          --radius: 20px; --radius-sm: 12px;
          --shadow: 0 18px 40px rgba(14,39,68,.10);
          --font-body: 'Satoshi', sans-serif;
        }
        .t90-report {
          font-family: var(--font-body);
          background: linear-gradient(180deg, #eef4f9 0%, #f7fafc 100%);
          color: var(--ink);
          line-height: 1.5;
        }
        .t90-report .report { max-width: 1100px; margin: 0 auto; padding: var(--space-8) var(--space-4) var(--space-16); }
        .t90-report .page {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 28px;
          box-shadow: var(--shadow);
          overflow: hidden;
          margin-bottom: var(--space-8);
        }
        /* Cover */
        .t90-report .cover {
          min-height: 92vh;
          position: relative;
          background: radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
                      linear-gradient(135deg, #A81026, #C4163A 55%, #7B0F2B);
          color: white;
        }
        .t90-report .cover-inner { padding: var(--space-16); display: flex; flex-direction: column; justify-content: space-between; min-height: 92vh; }
        .t90-report .brand { display: flex; align-items: center; justify-content: space-between; gap: var(--space-4); position: relative; z-index: 2; padding-right: clamp(0px, 38vw, 200px); }
        .t90-report .logo-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: clamp(180px, 42vw, 500px);
          height: clamp(90px, 21vw, 250px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
          pointer-events: none;
        }
        .t90-report .logo-panel-inner {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          position: relative;
          z-index: 2;
          padding-left: clamp(20px, 5vw, 60px);
          padding-bottom: clamp(8px, 2vw, 25px);
        }
        .t90-report .logo-img { height: clamp(30px, 6vw, 65px); width: auto; object-fit: contain; flex-shrink: 0; }
        .t90-report .badge {
          border: 1px solid rgba(255,255,255,.18);
          background: rgba(255,255,255,.08);
          padding: clamp(.3rem, .8vw, .6rem) clamp(.5rem, 1.2vw, 1rem);
          border-radius: 999px;
          font-size: clamp(0.58rem, 1.5vw, 0.85rem);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .t90-report .cover-title { margin-top: var(--space-16); }
        .t90-report .cover-title h2 {
          margin: 0 0 var(--space-4);
          font-size: clamp(2.6rem, 5vw, 4.8rem);
          line-height: .96;
          max-width: 10ch;
          border-left: 6px solid #fff;
          padding-left: 1rem;
        }
        .t90-report .cover-title p { font-size: var(--text-lg); max-width: 100%; color: rgba(255,255,255,.84); text-align: justify; }
        .t90-report .cover-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-4); margin-top: var(--space-12); }
        .t90-report .meta { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.16); border-radius: 18px; padding: 1rem 1.1rem; font-size: 1.2rem; }
        .t90-report .meta span { display: block; font-size: var(--text-xs); color: rgba(255,255,255,.66); margin-bottom: .25rem; }
        .t90-report .meta strong { font-size: var(--text-base); color: white; }
        .t90-report .cover-badges { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: var(--space-8); }
        .t90-report .cover-badge { background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.18); padding: .5rem .9rem; border-radius: 999px; font-size: 0.95rem; }
        .t90-report .footer-note { display: flex; justify-content: space-between; gap: var(--space-4); color: rgba(255,255,255,.72); font-size: var(--text-xs); margin-top: var(--space-10); }
        /* Page inner */
        .t90-report .page-inner { padding: var(--space-10); }
        .t90-report .topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: var(--space-4); margin-bottom: var(--space-8); }
        .t90-report .section-kicker { color: var(--blue); font-size: var(--text-xs); text-transform: uppercase; letter-spacing: .12em; font-weight: 700; }
        .t90-report .section-title { margin: .35rem 0 0; font-size: var(--text-xl); color: var(--navy); border-left: 5px solid #C4163A; padding-left: .75rem; }
        .t90-report .summary-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }

        /* Tier legend */
        .t90-report .legend-row { display:flex; gap:.85rem; flex-wrap:wrap; margin-top:.6rem; }
        .t90-report .legend-item { flex:1 1 190px; border-radius:10px; padding:.85rem 1rem; background:#F2F2F2; border-left:5px solid #ccc; }
        .t90-report .legend-item .lg-title { font-weight:700; font-size:0.85rem; color:#11304F; }
        .t90-report .legend-item .lg-range { font-size:0.72rem; color:#6b7280; margin-top:.15rem; }
        .t90-report .legend-item.dev  { border-color:#A80D2C; }
        .t90-report .legend-item.emg  { border-color:#D79A1E; }
        .t90-report .legend-item.est  { border-color:#5b7ba8; }
        .t90-report .legend-item.core { border-color:#1F6B3F; background:#eaf5ee; }

        /* Priority list */
        .t90-report .priority-list div { font-size:0.96rem; margin-top:.85rem; line-height:1.75; color:#334155; }
        .t90-report .priority-list b { color:#11304F; }

        /* Readiness donut + sub-scores */
        .t90-report .readiness-flex { display:flex; gap:2.25rem; align-items:center; flex-wrap:wrap; }
        .t90-report .donut-wrap { position:relative; width:220px; height:220px; flex:0 0 220px; }
        .t90-report .donut-center { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
        .t90-report .donut-center .pct { font-size:2.1rem; font-weight:800; color:#A80D2C; }
        .t90-report .donut-center .lbl { font-size:0.7rem; color:#6b7280; text-transform:uppercase; letter-spacing:.03em; }
        .t90-report .sub-scores { display:flex; gap:.85rem; flex:1; min-width:260px; }
        .t90-report .sub-score-card { flex:1; border-radius:12px; padding:1rem; background:#F2F2F2; }
        .t90-report .sub-score-card .n { font-size:1.65rem; font-weight:800; }
        .t90-report .sub-score-card .n.mindset { color:#A80D2C; }
        .t90-report .sub-score-card .n.skill { color:#D79A1E; }
        .t90-report .sub-score-card .t { font-size:0.75rem; color:#6b7280; margin-top:.25rem; }
        .t90-report .tag { font-size:0.68rem; font-weight:700; padding:.2rem .55rem; border-radius:.75rem; white-space:nowrap; display:inline-block; margin-top:.4rem; }
        .t90-report .tag.dev  { background:#fbe6e6; color:#A80D2C; }
        .t90-report .tag.emg  { background:#fdf1da; color:#b8730f; }
        .t90-report .tag.est  { background:#e3eaf5; color:#3a5a8a; }
        .t90-report .tag.core { background:#dcf0e3; color:#155c31; }
        .t90-report .card { background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%); border: 1px solid var(--line); border-radius: var(--radius); padding: var(--space-6); }
        .t90-report .card h3 { margin: 0 0 .75rem; font-size: var(--text-lg); color: #A80D2C; }
        .t90-report .lead { color: #334155; font-size: var(--text-base); line-height: 1.75; margin: 0; text-align: left; }
        .t90-report .pill { display: inline-flex; align-items: center; gap: .45rem; padding: .5rem .85rem; border-radius: 999px; font-size: var(--text-xs); font-weight: 700; text-transform: uppercase; letter-spacing: .06em; white-space: nowrap; }
        .t90-report .pill.blue { background: #fef2f2; color: var(--blue); }
        /* KPI row */
        .t90-report .kpi-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1.5rem; }
        .t90-report .kpi { background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%); border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 1.25rem; }
        .t90-report .kpi .lbl { font-size: var(--text-xs); text-transform: uppercase; letter-spacing: .07em; color: var(--muted); }
        .t90-report .kpi .big { font-size: var(--text-xl); font-weight: 800; color: #A80D2C; margin: .3rem 0 .15rem; }
        .t90-report .kpi .note { font-size: var(--text-xs); color: var(--muted); }
        /* Radar */
        .t90-report .radar-wrap { display: flex; flex-direction: column; align-items: center; }
        .t90-report .radar-legend { display: flex; gap: 1.5rem; margin-top: .5rem; }
        .t90-report .radar-legend-item { display: flex; align-items: center; gap: .4rem; font-size: var(--text-sm); color: var(--navy); font-weight: 600; }
        .t90-report .radar-swatch { width: 12px; height: 12px; border-radius: 3px; }
        /* Dimension table */
        .t90-report .dim-table { display: grid; gap: .75rem; }
        .t90-report .dim-row { display: grid; grid-template-columns: 1fr 2fr auto; gap: 1rem; align-items: center; padding: .85rem 1rem; border: 1px solid var(--line); border-radius: 14px; background: #fff; }
        .t90-report .dim-name { display: flex; flex-direction: column; }
        .t90-report .dim-name strong { color: var(--navy); font-size: var(--text-sm); }
        .t90-report .dim-name span { color: var(--muted); font-size: var(--text-xs); }
        .t90-report .dim-bar-wrap { display: flex; align-items: center; gap: .6rem; }
        .t90-report .dim-bar-track { flex: 1; height: 10px; background: #e9eef3; border-radius: 999px; overflow: hidden; }
        .t90-report .dim-bar-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #A81026, #d6a23f); }
        .t90-report .dim-pct { font-size: var(--text-xs); font-weight: 700; color: var(--navy); width: 34px; text-align: right; }
        .t90-report .dim-chip { font-size: 11px; font-weight: 700; padding: .25rem .6rem; border-radius: 999px; white-space: nowrap; text-align: center; }
        /* Callout */
        .t90-report .callout { background: linear-gradient(135deg, #fff7f7, #fbeaea); border: 1px solid var(--line); border-radius: 18px; padding: 1.25rem 1.5rem; color: #334155; text-align: justify; }
        /* Back cover */
        .t90-report .back-cover { color: white; }
        .t90-report .back-cover .page-inner { display: flex; flex-direction: column; justify-content: space-between; }
        .t90-report .back-cover-header { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 0.5rem; padding: clamp(0.9rem, 2.5vw, 1.5rem) clamp(0.9rem, 3vw, 2.5rem); border-bottom: 1px solid rgba(255,255,255,0.15); }
        .t90-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem, 4vw, 3.25rem); color: #FFFFFF; }
        .t90-report .closing p { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(0.85rem, 2vw, 1.2rem); text-align: justify; }
        .t90-report .contact-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(140px, 100%), 1fr)); gap: clamp(0.4rem, 1.2vw, 1rem); margin-top: var(--space-8); }
        .t90-report .contact { background: rgba(168,16,38,.1); border: 1px solid rgba(255,255,255,.16); border-radius: clamp(10px, 2vw, 16px); padding: clamp(0.6rem, 1.5vw, .9rem); min-width: 0; }
        .t90-report .contact span { display: block; color: rgba(255,255,255,.64); font-size: var(--text-xs); margin-bottom: .3rem; }
        .t90-report .contact strong { font-size: clamp(0.78rem, 1.6vw, 0.92rem); word-break: break-word; }
        .t90-report .light { color: rgba(255,255,255,.68); }
        @media print {
          .t90-report { background: white; }
          .t90-report .report { max-width: none; padding: 0; }
          .t90-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; border: 1px solid #ddd; }
        }
        @media (max-width: 768px) {
          .t90-report .report { padding: 1.25rem 0.75rem 3rem; }
          .t90-report .page { border-radius: 20px; margin-bottom: 1.25rem; }
          .t90-report .cover { min-height: 88vh; }
          .t90-report .cover-inner { padding: 2.25rem; min-height: 88vh; }
          .t90-report .cover-title { margin-top: 2.5rem; }
          .t90-report .cover-title h2 { font-size: clamp(2rem, 5vw, 3.2rem); }
          .t90-report .cover-grid { gap: 0.75rem; margin-top: 1.5rem; grid-template-columns: 1fr; }
          .t90-report .page-inner { padding: 1.75rem; }
          .t90-report .topbar { flex-wrap: wrap; gap: 0.5rem; }
          .t90-report .kpi-row { grid-template-columns: 1fr; }
          .t90-report .dim-row { grid-template-columns: 1fr; gap: .5rem; }
        }
        @media (max-width: 480px) {
          .t90-report .report { padding: 0.75rem 0.5rem 2.5rem; }
          .t90-report .cover { min-height: 100svh; }
          .t90-report .cover-inner { padding: 1.25rem; min-height: 100svh; }
          .t90-report .cover-title h2 { font-size: clamp(1.6rem, 7vw, 2.2rem); line-height: 1.08; }
          .t90-report .cover-title p { font-size: 0.875rem; }
          .t90-report .page-inner { padding: 1rem; }
          .t90-report .section-title { font-size: 1.25rem; }
        }
        /* ── Download Button ── */
        #t90-pdf-btn {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          z-index: 9999;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.75rem 1.4rem;
          background: linear-gradient(135deg, #F3AC26, #D79A1E);
          color: white;
          border: 1px solid #F3AC26;
          border-radius: 999px;
          box-shadow:
            0 8px 24px rgba(198,40,40,0.35),
            0 0 14px rgba(243,173,38,0.45);
          cursor: pointer;
          transition: transform 0.18s ease,
                      box-shadow 0.18s ease,
                      opacity 0.18s ease;
        }
        #t90-pdf-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(198,40,40,0.45);
        }
        #t90-pdf-btn:active:not(:disabled) { transform: translateY(0); }
        #t90-pdf-btn:disabled { opacity: 0.72; cursor: not-allowed; }
        #t90-pdf-btn .btn-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          flex-shrink: 0;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

        {/* ── Floating PDF Download Button ── */}
        <button
          id="t90-pdf-btn"
          onClick={handleDownloadPDF}
          disabled={isGenerating}
          aria-label="Download PDF Report"
        >
          {isGenerating ? (
            <>
              <span className="btn-spinner" />
              Generating PDF…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════ PAGE 1 — COVER ══════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* Logo panel — matches VakSurvey.jsx exactly */}
              <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                <div className="cover-title">
                  <h2>Transition 90</h2>
                  <p>
                    First-Time Manager Assessment — 36 mindset statements and 24 situational skill
                    questions, mapped to 12 mindset dimensions and 12 skill dimensions, to gauge
                    readiness for the transition into a first management role.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{respondent.name || '—'}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                  <div className="meta"><span>Overall Readiness</span><strong>{overall.pct}% · {bandInfo(overall.pct).label}</strong></div>
                </div>

                <div className="cover-badges">
                  {['Overall Readiness', 'Mindset Breakdown', 'Skill Breakdown', 'Dimension Radar'].map((b) => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note">
                <div>Based on the Transition 90 First-Time Manager Assessment framework.</div>
                <div>36 mindset items · 24 skill scenarios · 60 questions total</div>
              </div>
            </div>
          </section>

          {/* ══════════════ PAGE 2 — ASSESSMENT SUMMARY ══════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Assessment Summary</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: '-1rem', marginBottom: '1.25rem' }}>
                What the tiers below mean, and where this participant stands overall.
              </p>

              <div className="card">
                <strong style={{ fontSize: '0.85rem', color: '#11304F' }}>Reading the tiers used throughout this report</strong>
                <div className="legend-row">
                  <div className="legend-item dev">
                    <div className="lg-title">Development Priority</div>
                    <div className="lg-range">0–39% · needs focused coaching first</div>
                  </div>
                  <div className="legend-item emg">
                    <div className="lg-title">Emerging Strength</div>
                    <div className="lg-range">40–59% · present but inconsistent</div>
                  </div>
                  <div className="legend-item est">
                    <div className="lg-title">Established Strength</div>
                    <div className="lg-range">60–79% · reliable, close to reflex</div>
                  </div>
                  <div className="legend-item core">
                    <div className="lg-title">Core Strength</div>
                    <div className="lg-range">80–100% · near-automatic standard</div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: '1rem' }}>
                <div className="readiness-flex">
                  <Donut pct={overall.pct} />
                  <div className="sub-scores">
                    <div className="sub-score-card">
                      <div className="n mindset">{mindset.pct}%</div>
                      <div className="t">Mindset Score<br />36 items · 12 mindsets<br />
                        <span className={`tag ${bandInfo(mindset.pct).level === 'strong' ? 'core' : bandInfo(mindset.pct).level === 'estab' ? 'est' : bandInfo(mindset.pct).level === 'emerging' ? 'emg' : 'dev'}`}>{bandInfo(mindset.pct).label}</span>
                      </div>
                    </div>
                    <div className="sub-score-card">
                      <div className="n skill">{skill.pct}%</div>
                      <div className="t">Skill Score<br />24 scenarios · 12 skills<br />
                        <span className={`tag ${bandInfo(skill.pct).level === 'strong' ? 'core' : bandInfo(skill.pct).level === 'estab' ? 'est' : bandInfo(skill.pct).level === 'emerging' ? 'emg' : 'dev'}`}>{bandInfo(skill.pct).label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: '1rem' }}>
                <strong style={{ fontSize: '0.9rem', color: '#A80D2C', letterSpacing: '0.01em' }}>Your Readiness Snapshot</strong>
                <p className="lead" style={{ marginTop: '0.75rem' }}>{readinessNarrative(overall.pct, mindset.pct, skill.pct)}</p>
              </div>

              <div className="card priority-list" style={{ marginTop: '1rem' }}>
                <strong style={{ fontSize: '0.9rem', color: '#A80D2C', letterSpacing: '0.01em' }}>Mindsets and Skills to Prioritise</strong>
                {lowestMindset && (
                  <div><b>Mindset to develop:</b> {lowestMindset.label} ({lowestMindset.pct}%) is the lowest mindset score on this report
                    {lowestMindset.pct < 40 ? " — it's in Development Priority, the single highest-leverage conversation to have with this participant." : "."}</div>
                )}
                {lowestSkills.length > 0 && (
                  <div><b>Skills to add:</b> {joinList(lowestSkills.map((d) => `${d.label} (${d.pct}%)`))} {lowestSkills.length === 1 ? 'is' : 'are'} the lowest skill score{lowestSkills.length === 1 ? '' : 's'} on this report{lowestSkillPct < 40 ? ', in Development Priority' : ''}.</div>
                )}
                {(mindsetCoreStrengths.length > 0 || skillCoreStrengths.length > 0) && (
                  <div><b>Core strengths already in place:</b> {joinList([...mindsetCoreStrengths, ...skillCoreStrengths].map((d) => `${d.label} (${d.pct}%)`))} — no coaching needed here, these are near-automatic.</div>
                )}
                {lowestMindset && linkedRow && linkedShowsMatchingGap && (
                  <div style={{ marginTop: '0.6rem', color: '#6b7280' }}>Note: {lowestMindset.label} and its linked skill dimension are connected — addressing the mindset directly is likely to move the skill score as a side effect. See "The Pattern Behind the Profile" for the full picture.</div>
                )}
              </div>
            </div>
          </section>

          {/* ══════════════ PAGE 3 — MINDSET BREAKDOWN ══════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Mindset Breakdown</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: '-1.25rem', marginBottom: '1.5rem' }}>
                {mindset.pct}% overall · 12 dimensions · 3 statements each
              </p>

              {renderDimensionTable(mindset.dimensions)}

              <div className="callout" style={{ marginTop: '1.5rem' }}>
                <strong style={{ color: '#c62828' }}>Reading this table:</strong>
                <p style={{ marginTop: '0.5rem' }}>
                  Each dimension above is scored from 3 statements. Some are worded plainly, so agreeing
                  with them is a good sign. Others are worded as a trap — they describe a limiting belief,
                  so agreeing with them is actually a bad sign (these are the ones marked "reverse-scored").
                  A dimension under 40% means the answers leaned toward the limiting beliefs more often than
                  the healthy ones; 80%+ means they leaned toward the healthy belief almost every time.
                </p>
              </div>
            </div>
          </section>

          {/* ══════════════ PAGE 4 — SKILL BREAKDOWN ══════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Skill Breakdown</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: '-1.25rem', marginBottom: '1.5rem' }}>
                {skill.pct}% overall · 12 dimensions · 2 scenarios each
              </p>

              {renderDimensionTable(skill.dimensions)}

              <div className="callout" style={{ marginTop: '1.5rem' }}>
                <strong style={{ color: '#c62828' }}>Reading this table:</strong>
                <p style={{ marginTop: '0.5rem' }}>
                  Each dimension above is tested through two realistic scenarios, and every option in each
                  one was deliberately written to sound reasonable — there's no obviously wrong answer to
                  filter out on tone alone. Instead, every option is weighted on a 4-to-1 maturity ladder
                  against what an experienced manager would actually do. That's the key difference from the
                  mindset table: this isn't measuring what someone believes about managing — it's measuring
                  the call they'd make with a real team, a real deadline, and a real consequence in front of
                  them. Under 40% means the default instinct consistently reaches for a lower-maturity
                  option; 80%+ means the mature response has become the reflex, not the considered choice.
                </p>
              </div>
            </div>
          </section>

          {/* ══════════════ PAGE 5 — UNDERSTANDING YOUR PROFILE ══════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">Understanding Your Profile</h2>
                </div>
              </div>

              <div className="card">
                <h3>What This Assessment Measures</h3>
                <p className="lead">
                  Most first-time-manager failures don't come from not knowing what to do — they come from
                  knowing what to do and reverting to old habits anyway, the moment things get busy. That's
                  why this assessment scores two separate things instead of collapsing them into one number.
                </p>
                <p className="lead" style={{ marginTop: '0.85rem' }}>
                  The <strong>Mindset score</strong> measures what a person actually believes about the job
                  — not what they'd say in a training room, but what they agree or disagree with when a
                  statement is specific enough to expose the real belief underneath. This is the layer that
                  decides what a manager reaches for by default when no one is coaching them in the moment.
                </p>
                <p className="lead" style={{ marginTop: '0.85rem' }}>
                  The <strong>Skill score</strong> measures what they'd actually do — tested through
                  realistic, high-pressure scenarios where every option sounds plausible on first read, and
                  only one reflects the response an experienced manager would actually choose, weighted
                  against the alternatives on a 4-to-1 maturity ladder.
                </p>
                <p className="lead" style={{ marginTop: '0.85rem' }}>
                  Read together, the two scores answer a question a single number can't: is a gap a{' '}
                  <strong>belief problem</strong> (knows what's right, doesn't yet believe it under pressure)
                  or an <strong>execution problem</strong> (believes it, hasn't built the muscle to act on it)?
                </p>
              </div>

              <div className="card" style={{ border: '2px solid #A80D2C', marginTop: '1.5rem' }}>
                <h3 style={{ textTransform: 'uppercase', letterSpacing: '.05em', fontSize: '0.9rem' }}>
                  Strengths &amp; Priorities At a Glance
                </h3>
                {(mindsetCoreStrengths.length > 0 || skillCoreStrengths.length > 0) && (
                  <p className="lead">
                    <strong>Core Strengths (80%+):</strong>{' '}
                    {mindsetCoreStrengths.length > 0
                      ? `${joinList(mindsetCoreStrengths.map(coreStrengthLabel))} ${mindsetCoreStrengths.length === 1 ? 'is' : 'are'} the clearest mindset strength${mindsetCoreStrengths.length === 1 ? '' : 's'} — a high, near-automatic standard is already in place.`
                      : 'No mindset dimension has yet reached Core Strength (80%+).'}
                    {skillCoreStrengths.length > 0 && (
                      <> On the skill side, {joinList(skillCoreStrengths.map((d) => d.label))} score{skillCoreStrengths.length === 1 ? 's' : ''} 80%+ — a genuinely strong operational base.</>
                    )}
                  </p>
                )}
                {lowestMindset && (
                  <p className="lead">
                    <strong>Immediate Priority:</strong> {lowestMindset.label} ({lowestMindset.pct}%) is the lowest mindset score on this report
                    {lowestMindset.pct < 40 ? ', placing it in Development Priority' : ''} — worth a direct conversation before anything else.
                  </p>
                )}
                {lowestSkills.length > 0 && (
                  <p className="lead">
                    <strong>Secondary Priorities:</strong> {joinList(lowestSkills.map((d) => d.label))} {lowestSkills.length === 1 ? 'is' : 'are'}{' '}
                    tied at {lowestSkillPct}% — the lowest skill score{lowestSkills.length === 1 ? '' : 's'} on this report
                    {lowestSkillPct < 40
                      ? ', worth sequencing deliberately rather than tackling at once.'
                      : ', though still comfortably above the Development Priority threshold.'}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* ══════════════ PAGE 6 — THE PATTERN BEHIND THE PROFILE ══════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 6</div>
                  <h2 className="section-title">The Pattern Behind the Profile</h2>
                </div>
              </div>

              {lowestMindset && linkedRow && (
                <div className="card" style={{ border: '2px solid #A80D2C' }}>
                  <p className="lead">
                    {lowestMindset.label} ({lowestMindset.code}, <strong>{lowestMindset.pct}%</strong>) is the lowest mindset score on this report
                    {linkedSkillDims.length > 0 ? (
                      isLinkedAlsoLowestSkill ? (
                        <>
                          , and its linked skill dimension{linkedSkillDims.length === 1 ? '' : 's'} — {joinList(linkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} — {linkedSkillDims.length === 1 ? 'is' : 'are'} also tied for the lowest skill score on this report.
                        </>
                      ) : linkedBelowMidpoint ? (
                        <>
                          , and its linked skill dimension{linkedSkillDims.length === 1 ? '' : 's'} — {joinList(linkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} — {linkedSkillDims.length === 1 ? 'sits' : 'sit'} well below the midpoint too, though the single lowest skill score on this report is actually {joinList(lowestSkills.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))}.
                        </>
                      ) : strongLinkedSkillDims.length === linkedSkillDims.length ? (
                        <>
                          . Its linked skill dimension{linkedSkillDims.length === 1 ? '' : 's'} — {joinList(linkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} — {linkedSkillDims.length === 1 ? 'actually scores' : 'actually score'} well above the midpoint, so this particular gap hasn't shown up as a matching skill weakness yet
                        </>
                      ) : (
                        <>
                          . Of its linked skill dimensions, {joinList(weakLinkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} {weakLinkedSkillDims.length === 1 ? 'sits' : 'sit'} below the midpoint too, while {joinList(strongLinkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} {strongLinkedSkillDims.length === 1 ? 'actually scores' : 'actually score'} well above it
                        </>
                      )
                    ) : (
                      '.'
                    )}{' '}
                    {linkedShowsMatchingGap ? (
                      <>This is very likely one underlying pattern showing up in more than one place, rather than several unrelated development areas: {linkedRow.why.toLowerCase()}.</>
                    ) : (
                      <>That gap is worth naming directly even so, since {linkedRow.why.toLowerCase()} — the belief may simply not have caught up yet with the capability already being demonstrated.</>
                    )}
                  </p>
                  <p className="lead" style={{ marginTop: '0.75rem' }}>
                    {linkedShowsMatchingGap ? (
                      <>
                        A coaching conversation that addresses the mindset directly is likely to move the linked skill score{weakLinkedSkillDims.length === 1 ? '' : 's'} as a side effect —
                        making it the single highest-leverage conversation to have with this participant
                        {secondaryPriorities.length > 0 && (
                          <>, ahead of {joinList(secondaryPriorities.map((d) => d.label))}, which {secondaryPriorities.length === 1 ? "doesn't" : "don't"} share this root cause and can be sequenced separately.</>
                        )}
                      </>
                    ) : (
                      <>
                        A coaching conversation that addresses the mindset directly is unlikely to move an already-strong skill score, but it can help the participant's confidence catch up with evidence they're already producing in practice
                        {secondaryPriorities.length > 0 && (
                          <>. In the meantime, {joinList(secondaryPriorities.map((d) => d.label))} {secondaryPriorities.length === 1 ? 'is' : 'are'} worth prioritising as {secondaryPriorities.length === 1 ? 'a' : ''} genuine skill gap{secondaryPriorities.length === 1 ? '' : 's'} that {secondaryPriorities.length === 1 ? "doesn't" : "don't"} share this root cause.</>
                        )}
                      </>
                    )}
                  </p>
                </div>
              )}

              <h3 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#A80D2C', textTransform: 'uppercase', letterSpacing: '.05em', margin: '1.5rem 0 0.5rem' }}>
                Full Mindset ↔ Skill Map (for reference)
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#A80D2C', color: '#fff', textAlign: 'left', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.78rem' }}>Mindset Dimension</th>
                    <th style={{ background: '#A80D2C', color: '#fff', textAlign: 'left', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.78rem' }}>Related Skill Dimension(s)</th>
                    <th style={{ background: '#A80D2C', color: '#fff', textAlign: 'left', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.78rem' }}>Why they're linked</th>
                  </tr>
                </thead>
                <tbody>
                  {MINDSET_SKILL_MAP.map((row, i) => (
                    <tr key={row.m} style={i % 2 === 1 ? { background: '#F5F8FC' } : undefined}>
                      <td style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>{row.m} — {row.mLabel}</td>
                      <td style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>{row.sLabel}</td>
                      <td style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>{row.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ══════════════ PAGE 7 — DIMENSION RADAR ══════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 7</div>
                  <h2 className="section-title">Dimension Radar</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: '-1rem', marginBottom: '1.25rem' }}>
                Mindset and skill scores plotted across all 24 dimensions, on the revised 0–100% scale.
              </p>

              <div className="card">
                <div className="radar-wrap">
                  <Radar
                    series={[
                      { name: 'Mindset', codes: radar.mindset.codes, values: radar.mindset.values, fill: 'rgba(168,13,44,.16)', stroke: '#A80D2C' },
                      { name: 'Skill',   codes: radar.skill.codes,   values: radar.skill.values,   fill: 'rgba(215,154,30,.18)', stroke: '#D79A1E' },
                    ]}
                  />
                  <div className="radar-legend">
                    <div className="radar-legend-item"><span className="radar-swatch" style={{ background: '#A80D2C' }} /> Mindset</div>
                    <div className="radar-legend-item"><span className="radar-swatch" style={{ background: '#D79A1E' }} /> Skill</div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* ══════════════ BACK COVER ══════════════ */}
          <section className="page back-cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
                minHeight: '56vh',
              }}
            >
              <div className="back-cover-header">
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '.05em' }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
                  <div className="closing" style={{ padding: '0.1rem' }}>
                    <h2>The First 90 Days Set the Tone</h2>
                    <p>
                      An overall readiness score of {overall.pct}% reflects where this participant stands
                      today — not a ceiling. Use the mindset and skill breakdowns to prioritise coaching
                      conversations on the dimensions scoring "Emerging" or "Development priority" first.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,.06)', borderColor: 'rgba(255,255,255,.14)', color: 'white' }}>
                    <h3 style={{ color: 'white' }}>Practical Learning Design</h3>
                    <p className="lead" style={{ color: 'rgba(255,255,255,.84)' }}>
                      Prioritise coaching conversations around the lowest-scoring mindset and skill
                      dimensions first. Pair each with a real, current situation the participant is
                      facing so the practice sticks rather than staying theoretical.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>Transition 90 Assessment Report</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: '1.5rem', fontSize: '0.75rem' }}>
                    This report is generated from the Transition 90 First-Time Manager Assessment framework.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Hidden PDF generator (mounts only while generating) ── */}
      {pdfData && (
        <HiddenT90Report
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
};

export default T90Report;